// zone.js MUST be imported before bootstrapApplication.
// Without it, Angular has no way to detect when async operations (HTTP, setTimeout,
// Promises, RxJS subscriptions) complete, so templates never re-render automatically.
import 'zone.js';

import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import {
  TWENTY7TEC_OAUTH_MESSAGE_SOURCE,
  TWENTY7TEC_OAUTH_PENDING_KEY,
} from './app/pages/cases/case-view/sourcing/mortgage-sourcing/products/twenty7tec-oauth';

// ── twenty7tec OAuth callback interception ──────────────────────────────────
// After the user grants access, twenty7tec redirects the OAuth popup back to our
// registered redirect_uri (the site root) with `?code=...` appended. This runs
// BEFORE bootstrapApplication so the Angular router/guards never see that URL
// (otherwise root would redirect to /login|/error and drop the code).
//
// It only fires in the throwaway OAuth popup — guarded by window.opener (a normal
// top-level visit has none) plus a code/error param. It relays the result to the
// window that opened it (postMessage) and closes, skipping the app boot entirely.
//
// targetOrigin is '*' on purpose: the opener may be a different origin than this
// callback (e.g. a localhost dev tab opening the stage callback), so we cannot
// pin it. The message only ever goes to window.opener — the exact window that
// opened this popup — and carries a single-use code that still needs the backend
// to exchange it, so the exposure is minimal.
function handleTwenty7tecOAuthCallback(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    const hasPendingFlow = !!sessionStorage.getItem(TWENTY7TEC_OAUTH_PENDING_KEY);

    if (window.opener && !window.opener.closed && hasPendingFlow && (code || error)) {
      window.opener.postMessage(
        {
          source: TWENTY7TEC_OAUTH_MESSAGE_SOURCE,
          code: code ?? undefined,
          error: error ?? (code ? undefined : 'missing_code'),
        },
        '*',
      );
      window.close();
      return true;
    }
  } catch {
    // Any failure here just means we fall through to a normal app boot.
  }
  return false;
}

// ── Deployment-safe auto-reload ─────────────────────────────────────────────
// After a deployment, the browser may have old index.html cached which references
// stale JS chunk hashes. When dynamic import() fails to load those chunks (404),
// this listener auto-reloads the page so the user gets the new index.html with
// correct hashes — no manual hard refresh needed.
// The cooldown prevents infinite reload loops.
const RELOAD_COOLDOWN_MS = 60_000;
let lastChunkReload = 0;

function tryReloadForChunkFailure(reason: unknown, source: string): boolean {
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : String((reason as { message?: unknown } | null)?.message ?? '');

  const isChunkFailure =
    /Loading chunk.*failed|Failed to load module script|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
      message,
    );

  if (!isChunkFailure) {
    return false;
  }

  const now = Date.now();
  if (now - lastChunkReload <= RELOAD_COOLDOWN_MS) {
    return true;
  }

  lastChunkReload = now;
  console.warn(`[main] Detected chunk/module load failure from ${source} — auto-reloading.`);
  window.location.reload();
  return true;
}

window.addEventListener(
  'error',
  (event) => {
    // Only handle script / module load failures (e.g. chunk-not-found after deploy)
    const target = event.target as HTMLElement | null;
    if (target?.tagName === 'SCRIPT' || target?.tagName === 'LINK') {
      tryReloadForChunkFailure('Failed to load module script', 'window.error-target');
      return;
    }

    if (event instanceof ErrorEvent) {
      tryReloadForChunkFailure(event.error ?? event.message, 'window.error');
    }
  },
  true, // capture phase to catch bubbling script errors
);

// Vite emits this when a dynamic import preload fails after file graph invalidation.
window.addEventListener('vite:preloadError', (event: Event) => {
  const preloadEvent = event as Event & { payload?: unknown };
  tryReloadForChunkFailure(preloadEvent.payload, 'vite:preloadError');
});

// Zone.js surfaces NavigationCancelingError (redirect type) as an unhandled promise
// rejection before Angular's router catch-handler runs. This fires before AppErrorHandler
// gets a chance to suppress it, so we intercept it here with event.preventDefault().
window.addEventListener(
  'unhandledrejection',
  (event) => {
    const error = event.reason;

    if (tryReloadForChunkFailure(error, 'unhandledrejection')) {
      event.preventDefault();
      return;
    }

    if (
      error instanceof Error &&
      (error.message.includes('Redirecting to') ||
        (error as any).cancellationCode === 0 ||
        (error as any).type === 0)
    ) {
      event.preventDefault();
    }
  },
  true,
);

if (!handleTwenty7tecOAuthCallback()) {
  bootstrapApplication(App, appConfig).catch((err) => console.error(err));
}
